import { z } from "zod";

export const restrictionSchema = z.enum(["password_change", "mfa_enrolment"]);

/** Mirrors `CurrentUser` in docs/architecture/api-contracts.md. */
export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  tenantId: z.string().nullable(),
  tenantSlug: z.string().nullable(),
  isPlatformAdmin: z.boolean(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  /** Signed in with a password somebody else chose. */
  mustChangePassword: z.boolean().default(false),
  /**
   * What must be cleared before the session can be used (FR-ID-007). The server answers 403 to every
   * other route while this is set, so the app routes on it rather than collecting those 403s.
   */
  restricted: restrictionSchema.nullable().default(null),
  /** Whether a confirmed second factor exists. Filled by `/auth/me`; a session body leaves it false. */
  mfaEnrolled: z.boolean().default(false),
  /** The user's own phone. Filled by `/auth/me`, so the profile form starts from it. */
  phone: z.string().nullable().default(null),
});

const isoDateTime = z.iso.datetime({ offset: true, local: true });

/**
 * Mirrors `Session`. Returned by login and refresh. The refresh token is not here: it arrives as an
 * HttpOnly cookie script cannot read (ADR-0005), so the app holds only its expiry.
 */
export const sessionSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: isoDateTime,
  refreshTokenExpiresAt: isoDateTime,
  user: currentUserSchema,
});

export const loginCredentialsSchema = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});

/**
 * Mirrors `CustomerSelf`. A customer of the tenant, not one of its users: no roles, no email
 * required, and a `phone` the server has already masked (`+91••••••••10`) so a screenshot of the
 * app does not carry a whole number.
 */
export const currentCustomerSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  phone: z.string(),
  verifiedAt: isoDateTime,
});

/** Mirrors `CustomerSession`. The same token fields as a staff session, a different principal. */
export const customerSessionSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: isoDateTime,
  refreshTokenExpiresAt: isoDateTime,
  customer: currentCustomerSchema,
});

export const otpRequestSchema = z.object({
  phone: z.string().min(1, "Enter your mobile number"),
});

export const otpVerifySchema = z.object({
  phone: z.string().min(1),
  code: z.string().regex(/^\d{6}$/, "Enter the six-digit code"),
});

export type CurrentUser = z.infer<typeof currentUserSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type LoginCredentials = z.infer<typeof loginCredentialsSchema>;
export type CurrentCustomer = z.infer<typeof currentCustomerSchema>;
export type CustomerSession = z.infer<typeof customerSessionSchema>;
export type OtpRequest = z.infer<typeof otpRequestSchema>;
export type OtpVerify = z.infer<typeof otpVerifySchema>;

/**
 * Either kind of session. The store holds one of these without caring which: the four token fields
 * are the whole of what the refresh machinery reads, and they are identical.
 */
export type AnySession = Session | CustomerSession;

/** True for a staff session. Narrows the union for anything that needs the user. */
export function isUserSession(session: AnySession | null | undefined): session is Session {
  return session != null && "user" in session;
}

/**
 * The slug a session names, or null when it names none. A customer session carries no slug: the
 * booking app already knows which salon it is from its own host, and the server reads the tenant
 * from the header rather than from the token.
 */
export function tenantSlugOf(session: AnySession | null | undefined): string | null {
  return isUserSession(session) ? session.user.tenantSlug : null;
}

/**
 * Every principal by explicit code. A platform administrator's session lists the `platform.*`
 * codes their platform role holds (TDD-PA FR-PA-003, FR-PA-004); being a platform administrator
 * no longer implies every code, which is what lets the console hide an Owner's actions from a Viewer.
 */
export function hasPermission(user: CurrentUser | null | undefined, code: string): boolean {
  if (!user) return false;
  return user.permissions.includes(code);
}

/** Mirrors `MfaChallenge`: the password was right and a second factor is owed. Five minutes. */
export const mfaChallengeSchema = z.object({
  mfaToken: z.string().min(1),
  expiresAt: isoDateTime,
  factors: z.array(z.string()),
});

/** Mirrors `LoginOutcome`: exactly one of the two is set (200 with a session, 202 with a challenge). */
export const loginOutcomeSchema = z
  .object({
    session: sessionSchema.nullable(),
    challenge: mfaChallengeSchema.nullable(),
  })
  .refine((outcome) => (outcome.session === null) !== (outcome.challenge === null), {
    message: "A sign-in returns a session or a challenge, never both and never neither.",
  });

/** Mirrors `TotpEnrolment`. */
export const totpEnrolmentSchema = z.object({
  secret: z.string().min(1),
  otpauthUri: z.string().startsWith("otpauth://"),
});

/** Mirrors `RecoveryCodes`. Shown once; the server keeps only hashes. */
export const recoveryCodesSchema = z.object({
  recoveryCodes: z.array(z.string()).min(1),
});

export const mfaCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the six-digit code"),
});

export const recoveryCodeFormSchema = z.object({
  recoveryCode: z.string().trim().min(1, "Enter one of your recovery codes"),
});

/**
 * BCrypt reads 72 bytes, and the server refuses more (identity.password_too_long). Checked here so
 * the form says so before the round trip; the server's minimum may be higher than 12.
 */
const newPassword = z
  .string()
  .min(12, "Use at least 12 characters")
  .refine((value) => new TextEncoder().encode(value).length <= 72, "Use at most 72 characters");

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword,
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "The two passwords do not match",
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ["newPassword"],
    message: "Choose a password different from the current one",
  });

/** `GET /auth/sessions` (FR-ID-016): one row per refresh-token chain, this device flagged. */
export const sessionInfoSchema = z.object({
  chainId: z.string(),
  deviceLabel: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  isCurrent: z.boolean(),
});

const workspace = z.string().trim().toLowerCase().min(1, "Enter your workspace");

/** The forgot-password form. The server answers 202 whatever the address (FR-ID-021). */
export const forgotPasswordSchema = z.object({
  tenantSlug: workspace,
  email: z.string().trim().pipe(z.email("Enter a valid email address")),
});

/** Choosing a password from an emailed link: the reset and the invitation (FR-ID-021, FR-ID-025). */
export const setPasswordSchema = z
  .object({ tenantSlug: workspace, newPassword, confirmPassword: z.string() })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "The two passwords do not match",
  });

/** `PUT /auth/me` (FR-ID-016): the two things a user may change about themselves. */
export const profileSchema = z.object({
  fullName: z.string().trim().min(1, "Enter your name").max(200, "Use at most 200 characters"),
  phone: z
    .string()
    .trim()
    .max(40, "Use at most 40 characters")
    .optional()
    // A cleared field means "no phone", which the API takes as null.
    .transform((value) => (value === undefined || value === "" ? null : value)),
});

export type Restriction = z.infer<typeof restrictionSchema>;
export type MfaChallenge = z.infer<typeof mfaChallengeSchema>;
export type LoginOutcome = z.infer<typeof loginOutcomeSchema>;
export type TotpEnrolment = z.infer<typeof totpEnrolmentSchema>;
export type RecoveryCodes = z.infer<typeof recoveryCodesSchema>;
export type MfaCodeValues = z.infer<typeof mfaCodeSchema>;
export type RecoveryCodeValues = z.infer<typeof recoveryCodeFormSchema>;
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;
export type SessionInfo = z.infer<typeof sessionInfoSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type SetPasswordValues = z.infer<typeof setPasswordSchema>;
export type ProfileValues = z.input<typeof profileSchema>;
